import React, { useEffect, useState, useCallback } from 'react';
import { GroupBox } from '../shared/GroupBox';
import { StatusBadge } from '../shared/StatusBadge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { 
  CreditCard, RefreshCw, Plus, CheckCircle, IndianRupee, 
  Clock, AlertCircle, RotateCcw, Receipt, ExternalLink, Download, FileText, Printer
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useAuthStore } from '../../store/authStore';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function PaymentsPage() {
  const { user } = useAuthStore();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    description: ''
  });
  const [refundForm, setRefundForm] = useState({
    payment_id: '',
    amount: '',
    reason: ''
  });
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [razorpayConfig, setRazorpayConfig] = useState(null);

  // Load Razorpay script
  const loadRazorpayScript = useCallback(() => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/payments/history`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setPayments(data);
      }
    } catch (error) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/payments/config`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setRazorpayConfig(data);
      }
    } catch (error) {
      console.error('Failed to load payment config');
    }
  };

  useEffect(() => {
    fetchPayments();
    fetchConfig();
    loadRazorpayScript();
  }, [loadRazorpayScript]);

  const handleCreateAndPay = async () => {
    try {
      const amount = parseFloat(paymentForm.amount);
      if (!amount || amount < 1) {
        toast.error('Minimum payment amount is ₹1');
        return;
      }
      
      setLoading(true);
      
      // Create order
      const orderResponse = await fetch(`${API_URL}/api/payments/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          amount: amount,
          description: paymentForm.description || 'YellowGold Pro Payment'
        })
      });
      
      if (!orderResponse.ok) {
        const error = await orderResponse.json();
        throw new Error(error.detail || 'Failed to create order');
      }
      
      const orderData = await orderResponse.json();
      
      // Load Razorpay
      const isLoaded = await loadRazorpayScript();
      if (!isLoaded) {
        throw new Error('Failed to load Razorpay');
      }
      
      // Open Razorpay checkout
      const options = {
        key: orderData.key_id,
        amount: orderData.amount_paise,
        currency: orderData.currency,
        name: razorpayConfig?.name || 'YellowGold Pro',
        description: paymentForm.description || 'Payment',
        order_id: orderData.order_id,
        handler: async (response) => {
          // Verify payment
          try {
            const verifyResponse = await fetch(`${API_URL}/api/payments/verify`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature
              })
            });
            
            if (verifyResponse.ok) {
              toast.success('Payment successful!');
              setShowPayDialog(false);
              setPaymentForm({ amount: '', description: '' });
              fetchPayments();
            } else {
              const error = await verifyResponse.json();
              toast.error(error.detail || 'Payment verification failed');
            }
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: user?.name || '',
          email: user?.email || '',
          contact: user?.mobile || ''
        },
        theme: {
          color: razorpayConfig?.theme_color || '#DAA520'
        },
        modal: {
          ondismiss: () => {
            toast.info('Payment cancelled');
            fetchPayments();
          }
        }
      };
      
      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      toast.error(error.message || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRefund = async () => {
    try {
      if (!refundForm.payment_id) {
        toast.error('Payment ID is required');
        return;
      }
      
      setLoading(true);
      const response = await fetch(`${API_URL}/api/payments/refund`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          payment_id: refundForm.payment_id,
          amount: refundForm.amount ? parseFloat(refundForm.amount) : null,
          reason: refundForm.reason
        })
      });
      
      if (response.ok) {
        toast.success('Refund processed successfully');
        setShowRefundDialog(false);
        setRefundForm({ payment_id: '', amount: '', reason: '' });
        fetchPayments();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Refund failed');
      }
    } catch (error) {
      toast.error('Refund failed');
    } finally {
      setLoading(false);
    }
  };

  const openRefundDialog = (payment) => {
    setSelectedPayment(payment);
    setRefundForm({
      payment_id: payment.payment_id,
      amount: '',
      reason: ''
    });
    setShowRefundDialog(true);
  };

  // Download PDF receipt
  const downloadPdfReceipt = async (orderId) => {
    try {
      const response = await fetch(`${API_URL}/api/payments/${orderId}/receipt/pdf`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to download receipt');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `receipt_${orderId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Receipt downloaded');
    } catch (error) {
      toast.error(error.message || 'Failed to download receipt');
    }
  };

  // View/Print HTML receipt
  const viewHtmlReceipt = async (orderId) => {
    window.open(`${API_URL}/api/payments/${orderId}/receipt/html`, '_blank');
  };

  // Calculate totals
  const totalAmount = payments.reduce((sum, p) => sum + (p.status === 'COMPLETED' ? p.amount : 0), 0);
  const completedCount = payments.filter(p => p.status === 'COMPLETED').length;
  const pendingCount = payments.filter(p => p.status === 'CREATED').length;
  const refundedCount = payments.filter(p => p.status === 'REFUNDED' || p.status === 'PARTIALLY_REFUNDED').length;

  return (
    <div className="space-y-4 p-4" data-testid="payments-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold text-yellow-500">Payments</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchPayments}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button size="sm" onClick={() => setShowPayDialog(true)} data-testid="new-payment-btn">
            <Plus className="h-4 w-4 mr-1" />
            New Payment
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-lg border border-gray-700 bg-gray-800/50">
          <div className="flex items-center gap-2">
            <IndianRupee className="w-5 h-5 text-yellow-500" />
            <span className="text-sm text-gray-400">Total Collected</span>
          </div>
          <div className="text-2xl font-bold text-green-500">₹{totalAmount.toLocaleString()}</div>
        </div>
        
        <div className="p-4 rounded-lg border border-green-500/30 bg-green-900/10">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="text-sm text-gray-400">Completed</span>
          </div>
          <div className="text-2xl font-bold text-green-500">{completedCount}</div>
        </div>
        
        <div className="p-4 rounded-lg border border-amber-500/30 bg-amber-900/10">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-500" />
            <span className="text-sm text-gray-400">Pending</span>
          </div>
          <div className="text-2xl font-bold text-amber-500">{pendingCount}</div>
        </div>
        
        <div className="p-4 rounded-lg border border-blue-500/30 bg-blue-900/10">
          <div className="flex items-center gap-2">
            <RotateCcw className="w-5 h-5 text-blue-500" />
            <span className="text-sm text-gray-400">Refunded</span>
          </div>
          <div className="text-2xl font-bold text-blue-500">{refundedCount}</div>
        </div>
      </div>

      {/* Payment History */}
      <GroupBox title="Payment History" className="border-yellow-500/50">
        <div className="p-4">
          {loading && payments.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin text-yellow-500" />
            </div>
          ) : payments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No payments yet</p>
              <p className="text-sm">Click "New Payment" to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-3 px-4">Date</th>
                    <th className="text-left py-3 px-4">Order ID</th>
                    <th className="text-right py-3 px-4">Amount</th>
                    <th className="text-left py-3 px-4">Description</th>
                    <th className="text-center py-3 px-4">Status</th>
                    <th className="text-left py-3 px-4">Payment ID</th>
                    <th className="text-center py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr key={payment.order_id} className="border-b border-gray-800 hover:bg-gray-800/50">
                      <td className="py-3 px-4 text-gray-400">
                        {format(new Date(payment.created_at), 'dd/MM/yy HH:mm')}
                      </td>
                      <td className="py-3 px-4 font-mono text-xs">
                        {payment.order_id}
                      </td>
                      <td className="py-3 px-4 text-right font-mono font-semibold">
                        ₹{payment.amount.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-gray-400">
                        {payment.description || '-'}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          payment.status === 'COMPLETED' ? 'bg-green-500/20 text-green-500' :
                          payment.status === 'REFUNDED' ? 'bg-blue-500/20 text-blue-500' :
                          payment.status === 'FAILED' ? 'bg-red-500/20 text-red-500' :
                          'bg-amber-500/20 text-amber-500'
                        }`}>
                          {payment.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-mono text-xs text-gray-400">
                        {payment.payment_id || '-'}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-center gap-1">
                          {payment.status === 'COMPLETED' && (
                            <>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => downloadPdfReceipt(payment.order_id)}
                                className="text-yellow-500 hover:text-yellow-400 h-8 px-2"
                                title="Download PDF Receipt"
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => viewHtmlReceipt(payment.order_id)}
                                className="text-gray-400 hover:text-gray-300 h-8 px-2"
                                title="View & Print Receipt"
                              >
                                <Printer className="w-4 h-4" />
                              </Button>
                              {payment.payment_id && (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => openRefundDialog(payment)}
                                  className="text-blue-500 hover:text-blue-400 h-8 px-2"
                                  title="Refund"
                                >
                                  <RotateCcw className="w-4 h-4" />
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </GroupBox>

      {/* New Payment Dialog */}
      <Dialog open={showPayDialog} onOpenChange={setShowPayDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-yellow-500" />
              New Payment
            </DialogTitle>
            <DialogDescription>
              Enter payment details to proceed with Razorpay checkout
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Amount (₹) *</Label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input 
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                  placeholder="Enter amount"
                  className="pl-10"
                  min="1"
                  data-testid="payment-amount-input"
                />
              </div>
            </div>
            
            <div>
              <Label>Description (Optional)</Label>
              <Input 
                value={paymentForm.description}
                onChange={(e) => setPaymentForm({...paymentForm, description: e.target.value})}
                placeholder="Payment description"
                data-testid="payment-description-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateAndPay}
              disabled={loading || !paymentForm.amount}
              data-testid="pay-now-btn"
            >
              {loading ? 'Processing...' : 'Pay Now'}
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog open={showRefundDialog} onOpenChange={setShowRefundDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-blue-500" />
              Process Refund
            </DialogTitle>
            <DialogDescription>
              Refund payment {selectedPayment?.payment_id}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {selectedPayment && (
              <div className="p-3 bg-gray-800 rounded-lg">
                <div className="flex justify-between">
                  <span className="text-gray-400">Original Amount</span>
                  <span className="font-semibold">₹{selectedPayment.amount.toLocaleString()}</span>
                </div>
              </div>
            )}
            
            <div>
              <Label>Refund Amount (₹)</Label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input 
                  type="number"
                  value={refundForm.amount}
                  onChange={(e) => setRefundForm({...refundForm, amount: e.target.value})}
                  placeholder={`Max: ${selectedPayment?.amount || 0}`}
                  className="pl-10"
                  max={selectedPayment?.amount}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Leave empty for full refund</p>
            </div>
            
            <div>
              <Label>Reason (Optional)</Label>
              <Input 
                value={refundForm.reason}
                onChange={(e) => setRefundForm({...refundForm, reason: e.target.value})}
                placeholder="Reason for refund"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRefundDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRefund}
              disabled={loading}
              variant="destructive"
            >
              {loading ? 'Processing...' : 'Process Refund'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PaymentsPage;
